import { useEffect, useState } from 'react';
import {
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { tenantIdFromUsageDayPath, type Period } from '@etiquetachef/types';
import { firestore } from '@/lib/firebase';
import { isReportExcludedTenant } from '@/features/tenants/testTenantFilter';
import { reportNonFatal, serializeError } from '@/lib/errorReport';

const TAG = 'admin/usage/useUsage';
const TOP_LIMIT = 20;

export type UsageBucket = 'B0_10' | 'B11_50' | 'B51_200' | 'B201_1000' | 'B1000_PLUS';

export type TopTenantRow = {
  tenantId: string;
  tenantName: string;
  countryCode: string;
  totalLabels: number;
};

export type UsageHeatCell = {
  yyyymmdd: string;
  weekday: number; // 0=Sun..6=Sat
  totalLabels: number;
};

export type UsageState =
  | { kind: 'LOADING' }
  | { kind: 'ERROR'; error: string }
  | {
      kind: 'READY';
      topTenants: TopTenantRow[];
      distribution: Record<UsageBucket, number>;
      heatCells: UsageHeatCell[];
    };

function bucketFor(total: number): UsageBucket {
  if (total <= 10) return 'B0_10';
  if (total <= 50) return 'B11_50';
  if (total <= 200) return 'B51_200';
  if (total <= 1000) return 'B201_1000';
  return 'B1000_PLUS';
}

function yyyymmddToWeekday(yyyymmdd: string): number {
  const yyyy = Number(yyyymmdd.slice(0, 4));
  const mm = Number(yyyymmdd.slice(4, 6)) - 1;
  const dd = Number(yyyymmdd.slice(6, 8));
  return new Date(Date.UTC(yyyy, mm, dd)).getUTCDay();
}

/**
 * Carrega dados do dashboard /usage:
 *  - top 20 tenants por total de etiquetas no período (collGroup
 *    days no range, somar por tenant).
 *  - distribuição por bucket de uso (mês corrente: somar por
 *    tenant, classificar).
 *  - heatmap simplificado: dia × dia-da-semana × volume (não temos
 *    granularidade horária no agregado days). UI pode renderizar
 *    como matriz weekday × week-of-period.
 *
 * Origem: sem issue — commit b7ad9714a, 2026-05-04 (rota /dashboard/usage).
 *
 * Contrato: `period` (datas `YYYY-MM-DD`) → `UsageState`, leitura one-shot
 * por mudança de período. A distribuição soma os mesmos totais do período
 * (`totalsByTenant`), não o mês corrente que o item acima cita — divergência
 * entre comentário e código registrada na #926, sem decisão de qual vale.
 * Erro de query → `ERROR` só com `console.error` (sem report para o PostHog;
 * achado também registrado na #926).
 *
 * Usado por: `UsageDashboardPage.tsx` (rota `/dashboard/usage` em
 * `routes/router.tsx`; web `apps/admin`, super-admin), que repassa o estado a
 * `TopTenantsTable`, `DistributionChart` e `UsageHeatmap`.
 *
 * Arquitetura: só leitura. A collection-group query `days` é autorizada por
 * `match /{path=**}/days/{yyyymmdd}` de `firebase/firestore.rules`
 * (`isSuperAdmin()`); o intervalo em `yyyymmdd` usa o `fieldOverrides`
 * `days.yyyymmdd` de escopo COLLECTION_GROUP em
 * `firebase/firestore.indexes.json` (sem ele, `failed-precondition`).
 *
 * Dados (anonimizado):
 *   tenants/{tenantId}/billing/usage/days/{yyyymmdd}
 *     { "yyyymmdd": "20261001", "labelsPrinted": 42 }
 *   — `yyyymmdd` é string compacta (comparação lexicográfica = cronológica);
 *     o dia é o bucket UTC do trigger `recordLabelPrinted`, não o fuso do
 *     tenant (ver `docs/label-counters.md`).
 * O CG `days` também casa `aggregations/*` e `crm_lookup_quota/*`:
 * `tenantIdFromUsageDayPath` (`packages/types/src/usagePaths.ts`) descarta
 * tudo que não é bucket de tenant, e é isso que impede somar os agregados
 * globais por cima dos tenants.
 *
 * Tenant de TESTE (#1008, decisão do owner: o simulador de uso sai de todos
 * os painéis) não entra em top 20, distribuição nem heatmap — filtro pelo
 * cadastro (`isReportExcludedTenant` só com o id; este hook não lê
 * assinatura, então sandbox não cadastrado continua contando — aceito na
 * #1008).
 *
 * Dicas: o lookup de nome/país vai em lotes de 30 (`__name__ in`, limite do
 * Firestore para `in`). Só os 20 do top são resolvidos.
 *
 * Falhas conhecidas:
 *  - até 2026-10-19 (#1008): IT-98765432103 e BR-01010001000180 (simulador)
 *    lideravam o top 20 e inflavam o heatmap.
 *
 * Issues: #926 (divergências acima), #1008 (tenant de teste fora).
 *
 * Histórico:
 *  - 2026-10-19 20:57 +0200 — (#1008) exclui tenant de teste.
 */
export function useUsage(period: Period): UsageState {
  const [state, setState] = useState<UsageState>({ kind: 'LOADING' });

  const periodFrom = period.from;
  const periodTo = period.to;

  useEffect(() => {
    const guard = { cancelled: false };
    setState({ kind: 'LOADING' });

    async function load(): Promise<void> {
      try {
        const fromCompact = periodFrom.replace(/-/g, '');
        const toCompact = periodTo.replace(/-/g, '');

        const daysSnap = await getDocs(
          query(
            collectionGroup(firestore, 'days'),
            where('yyyymmdd', '>=', fromCompact),
            where('yyyymmdd', '<=', toCompact),
          ),
        );

        const totalsByTenant = new Map<string, number>();
        const cellsByKey = new Map<string, UsageHeatCell>();
        for (const d of daysSnap.docs) {
          const tid = tenantIdFromUsageDayPath(d.ref.path);
          // #1008: tenant de teste fora do top, da distribuição e do heatmap.
          if (!tid || isReportExcludedTenant(tid)) continue;
          const labels = (d.get('labelsPrinted') as number | undefined) ?? 0;
          const yyyymmdd = (d.get('yyyymmdd') as string | undefined) ?? '';
          if (!yyyymmdd) continue;
          totalsByTenant.set(tid, (totalsByTenant.get(tid) ?? 0) + labels);
          const cell = cellsByKey.get(yyyymmdd) ?? {
            yyyymmdd,
            weekday: yyyymmddToWeekday(yyyymmdd),
            totalLabels: 0,
          };
          cell.totalLabels += labels;
          cellsByKey.set(yyyymmdd, cell);
        }

        // Distribuição (todos os tenants, não só os do top).
        const distribution: Record<UsageBucket, number> = {
          B0_10: 0,
          B11_50: 0,
          B51_200: 0,
          B201_1000: 0,
          B1000_PLUS: 0,
        };
        for (const total of totalsByTenant.values()) {
          distribution[bucketFor(total)]++;
        }

        // Top 20.
        const sortedIds = Array.from(totalsByTenant.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, TOP_LIMIT);
        const tenantIds = sortedIds.map(([id]) => id);

        // Lookup tenant info.
        const nameById = new Map<string, string>();
        const countryById = new Map<string, string>();
        for (let i = 0; i < tenantIds.length; i += 30) {
          const slice = tenantIds.slice(i, i + 30);
          if (slice.length === 0) continue;
          const tenantsSnap = await getDocs(
            query(collection(firestore, 'tenants'), where('__name__', 'in', slice)),
          );
          for (const t of tenantsSnap.docs) {
            const data = t.data() as Record<string, unknown>;
            nameById.set(t.id, typeof data.name === 'string' ? data.name : t.id);
            countryById.set(
              t.id,
              typeof data.countryCode === 'string' ? data.countryCode : '',
            );
          }
        }

        const topTenants: TopTenantRow[] = sortedIds.map(([id, total]) => ({
          tenantId: id,
          tenantName: nameById.get(id) ?? id,
          countryCode: countryById.get(id) ?? '',
          totalLabels: total,
        }));

        if (guard.cancelled) return;
        setState({
          kind: 'READY',
          topTenants,
          distribution,
          heatCells: Array.from(cellsByKey.values()).sort((a, b) =>
            a.yyyymmdd.localeCompare(b.yyyymmdd),
          ),
        });
      } catch (err) {
        if (guard.cancelled) return;
        console.error(`${TAG}: failed`, err);
        // #1021 (2026-10-23): a falha só ia para o console — invisível em produção (sem DevTools do cliente).
        reportNonFatal('admin_use_usage_failed', {
          site: 'failed',
          code: (err as { code?: string }).code ?? null,
          ...serializeError(err),
        });
        setState({
          kind: 'ERROR',
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }

    void load();
    return (): void => {
      guard.cancelled = true;
    };
  }, [periodFrom, periodTo]);

  return state;
}
